import { formatPrice } from "@/lib/string";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import {
	Carousel,
	CarouselApi,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/ui/carousel";

type ProductImage = {
	id: number;
	fullUrl: string;
};

export default function MerchProductHeader({
	productName,
	description,
	price,
	basePrice,
	images,
	badges,
}: {
	productName: string;
	description?: string | null;
	basePrice: number;
	price?: number;
	images: ProductImage[];
	badges?: string[];
}) {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		if (!api) return;

		setCurrent(api.selectedScrollSnap());

		api.on("select", () => {
			setCurrent(api.selectedScrollSnap());
		});
	}, [api]);

	const imageCount = images.length;

	return (
		<div className="relative overflow-hidden">
			<div className="flex w-full flex-col justify-between bg-white px-4 py-25 pb-6 [clip-path:url(#wave-clip)] md:flex-row md:px-37 md:pt-61 md:pb-10">
				{/* Left: Text Content */}
				<div className="flex flex-col gap-8 md:gap-16 md:w-1/2">
					<div className="bg-blue w-fit rounded-2xl p-3 md:p-4">
						<p className="web-heading-h4 text-blue-500">
							{price != null
								? formatPrice(price, "AUD")
								: formatPrice(basePrice, "AUD")}
						</p>
					</div>

					<div className="flex flex-col gap-6">
						<div className="hidden max-sm:block">
							<p className="web-heading-h3">{productName}</p>
						</div>
						<div className="hidden md:block">
							<p className="web-heading-h1">{productName}</p>
						</div>

						{badges && badges.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{badges.slice(0, 6).map((badge) => (
									<span
										key={badge}
										className="rounded-full bg-[rgba(46,_127,_219,_0.1)] px-4 py-2 text-sm text-blue-700"
									>
										{badge}
									</span>
								))}
							</div>
						)}

						{description && <p className="web-body-b1 md:max-w-[50ch]">{description}</p>}
					</div>
				</div>

				{/* Right: Image */}
				<div className="w-full rounded-[32px] bg-white max-sm:pt-5 max-sm:pb-6 md:w-1/2 md:pb-6">
					{imageCount === 0 ? (
						<div className="h-80 w-full rounded-[32px] bg-gray-100 md:h-112" />
					) : (
						<>
							<div className="h-80 md:h-112">
								<Carousel setApi={setApi} opts={{ loop: imageCount > 1 }}>
									<CarouselContent className="ml-0">
										{images.map((img, index) => (
											<CarouselItem key={img.id} className="pl-0 h-80 md:h-112">
												<img
													src={img.fullUrl}
													alt={`${productName} image ${index + 1}`}
													className="h-full w-full rounded-[32px] object-cover"
												/>
											</CarouselItem>
										))}
									</CarouselContent>

									{imageCount > 1 && (
										<>
											<CarouselPrevious className="left-4 bg-white/80 hover:bg-white" />
											<CarouselNext className="right-4 bg-white/80 hover:bg-white" />
										</>
									)}
								</Carousel>
							</div>

							{imageCount > 1 && (
								<div className="mt-5 flex items-center justify-center gap-2">
									{Array.from({ length: imageCount }).map((_, index) => (
										<button
											key={index}
											onClick={() => api?.scrollTo(index)}
											className={cn(
												"transition-all duration-300",
												current === index
													? "h-3 w-3 rounded-full bg-blue-700"
													: "h-2 w-2 rounded-full bg-blue-900-20"
											)}
											aria-label={`Go to image ${index + 1}`}
										/>
									))}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
